import { FormControl } from '@angular/forms';
import moment, { Moment } from 'moment';
import { Subject } from 'rxjs';

import { DATE_FORMAT_FRIENDLY, DATE_FORMAT_ODATA, DATE_FORMAT_SAP } from '@shared-constants/date-format-constants';
import { NON_DIGITS_PATTERN } from '@shared-constants/pattern-constants';

export class DateHandler {
  selectedDate: FormControl<moment.Moment>;
  previousDate = moment();
  weDateChangedSubject$ = new Subject();

  constructor(date?: string) {
    if (!date) this.selectedDate = new FormControl(moment().startOf('day'), { nonNullable: true });
    this.selectedDate = new FormControl(moment(date).startOf('day'), { nonNullable: true });
    this.syncPreviousDate();
    this.selectedDate.valueChanges.subscribe((value) => {
      if (this.isSameWeek(value, this.previousDate)) return;
      this.weDateChangedSubject$.next(this.getFilterString(value));
      this.syncPreviousDate();
    });
  }

  getHumanReadableDateString(momentObj?: moment.Moment): string {
    if (!momentObj) return this.selectedDate.value.format(DATE_FORMAT_FRIENDLY);
    return momentObj.format(DATE_FORMAT_FRIENDLY);
  }

  //We use an overload methods here so that TS can send correct typing back depending on what parameters are given
  getSapDateString(shouldReturnMoment?: false): string;
  getSapDateString(shouldReturnMoment?: boolean): string | Moment;
  getSapDateString<T extends boolean | undefined>(shouldReturnMoment?: T): string | Moment {
    const momentObj = this.selectedDate.value;
    const clone = momentObj.clone();
    return shouldReturnMoment ? clone : clone.format(DATE_FORMAT_SAP);
  }

  getWeDate(momentObj?: moment.Moment): moment.Moment {
    if (momentObj) return momentObj.day(6);
    else return moment(this.selectedDate.value.clone().day(6));
  }

  getWeekStartingDate(momentObj?: moment.Moment): moment.Moment {
    if (moment.isMoment(momentObj)) return momentObj.day(0);
    else return moment(this.selectedDate.value.clone().day(0));
  }

  getOdataString(momentObj?: moment.Moment): string {
    if (momentObj) return momentObj.format(DATE_FORMAT_ODATA);
    else return this.selectedDate.value.clone().format(DATE_FORMAT_ODATA);
  }

  getFilterString(momentObj?: moment.Moment): string {
    if (momentObj) return `datetime'${this.getOdataString(momentObj)}'`;
    else return `datetime'${this.getOdataString()}'`;
  }

  getControl(): FormControl<moment.Moment> {
    return this.selectedDate;
  }

  getMoment(): moment.Moment {
    return this.selectedDate.value;
  }

  syncPreviousDate(): void {
    this.previousDate = this.selectedDate.value.clone();
  }

  isSameDay(item1: moment.Moment, item2: moment.Moment): boolean {
    return moment(item1).utc().isSame(item2, 'day');
  }

  isSameWeek(item1: moment.Moment, item2: moment.Moment = this.selectedDate.value): boolean {
    return moment(item1).isSame(item2, 'week');
  }

  convertSapDateToMoment(sapDate: string): moment.Moment {
    const string = sapDate.replace(NON_DIGITS_PATTERN, '');
    const number = Number(string);
    const date = new Date(number);
    return moment(date).utc();
  }

  setDate(date: string, format: string): void {
    const newDate = moment(date, format);
    if (!newDate.isValid()) throw Error("Invalid date format, or value didn't match the format.");
    this.selectedDate.setValue(newDate);
  }

  setToNow(): void {
    this.selectedDate.setValue(moment());
  }

  revert(): void {
    this.selectedDate.setValue(this.previousDate);
  }

  getDayOfWeek(): number {
    //have to force the type here since this relies on on untyped form control, so we just have to hope it has a value and it's correct
    const momentObj = this.selectedDate.value;
    return momentObj.day();
  }
}
